import { useCallback, useContext } from 'react';
import { EventAccordionItemProps } from 'src/components/Accordions';
import { BackSection } from 'src/components/common/BackSection';
import { EventHeaderWithActions } from 'src/components/Events/EventHeader';
import { EventHeaderNavTabs } from 'src/components/Events/EventHeader/EventHeaderNavTabs';
import { InventoryEventActionDropdown } from 'src/components/Listings/InventoryActionDropdown';
import { BoostListingButton } from 'src/components/SponsoredListings/BoostListingButton';
import { useEventHubContext } from 'src/contexts/EventHubContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useEventMapConfigMetadata } from 'src/hooks/api/useEventMapConfigMetadata';
import { useGetEventFullInfo } from 'src/hooks/useGetEventFullInfo';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { Event, Feature, Listing } from 'src/WebApiController';

import { NewEventMapBanner } from '../../Banners/NewEventMapBanner';
import { EventVenueMapModal } from '../../EventVenueMap/EventVenueMapModal';
import { InventoryEventTabs } from '../../InventoryEventTabs';
import { useInventoryEventLocalContext } from '../InventoryEventLocalContext';
import { EnterCompanionMode } from './EventActions/EnterCompanionMode';
import { LaunchAddSeatSaver } from './EventActions/LaunchAddSeatSaver';
import { LaunchMergeListings } from './EventActions/LaunchMergeListings';
import { MarkEventAsReviewed } from './EventActions/MarkEventAsReviewed';
import * as styles from './InventoryEventHeader.css';
import { EventHeaderWithNav } from './InventoryEventHeaderWithNav';

export const InventoryEventHeader = ({
  event,
  inventoryAccordionItem,
  returnUrl,
}: {
  event: Event;
  inventoryAccordionItem?: EventAccordionItemProps<Listing>;
  returnUrl?: string;
}) => {
  const { venue, performer } = useGetEventFullInfo(event);
  const { isOnSidePanel } = useEventHubContext();

  const isMobile = useMatchMedia('mobile');

  const { setModal } = useContext(ModalContext);
  const openEventMapView = useCallback(() => {
    setModal({
      children: <EventVenueMapModal event={event} />,
      size: 'fullscreen',
    });
  }, [event, setModal]);

  const { eventMapConfigQuery } = useEventMapConfigMetadata(event.viagId);

  const {
    defaultTab,
    activeTab,
    setActiveTab,
    eventContents,
    onUpdateDefaultType,
  } = useInventoryEventLocalContext();

  return (
    <Stack
      className={styles.tilesSection}
      gap={isMobile ? 's' : 'm'}
      direction="column"
    >
      {isMobile && (
        <Stack alignItems="center" width="full">
          <BackSection
            returnUrl={returnUrl ?? '/inventory'}
            returnState={{
              fromViagVirtId: event.virtId,
            }}
            iconOnly
          />
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <EventHeaderNavTabs<InventoryEventTabs>
              defaultTab={defaultTab}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              onUpdateDefaultTab={onUpdateDefaultType}
              tabs={eventContents
                .filter((tab) => !tab.hide)
                .map((tab) => ({
                  value: tab.id,
                  contentId: tab.contentId,
                }))}
            />
          </div>
        </Stack>
      )}
      <NewEventMapBanner
        openEventMapView={openEventMapView}
        eventMapConfigQuery={eventMapConfigQuery}
      />

      <Stack gap="m" direction="column">
        <EventHeaderWithActions
          event={event}
          performer={performer}
          venue={venue}
          actions={
            <EventHeaderActions
              event={event}
              inventoryAccordionItem={inventoryAccordionItem}
            />
          }
        />
        {!isOnSidePanel && <EventHeaderWithNav event={event} venue={venue} />}
      </Stack>
    </Stack>
  );
};

const EventHeaderActions = ({
  event,
  inventoryAccordionItem,
}: {
  event: Event;
  inventoryAccordionItem?: EventAccordionItemProps<Listing>;
}) => {
  const isMobile = useMatchMedia('mobile');
  const hasSponsoredListingsV2Feature = useUserHasFeature(
    Feature.SponsoredListingsV2
  );

  const { enabled } = useEventHubContext();
  return (
    <Stack direction="row" gap={isMobile ? 's' : 'm'} alignItems="center">
      {hasSponsoredListingsV2Feature && event.viagId && (
        <BoostListingButton
          shape="rect"
          showIcon
          height={vars.spacing['3xl']}
          eventId={event.viagId}
        />
      )}
      {!isMobile && (
        <Stack direction="row" alignItems="center">
          <LaunchAddSeatSaver event={event} />
          {enabled && <EnterCompanionMode event={event} />}
          <LaunchMergeListings
            event={event}
            entityCount={inventoryAccordionItem?.entityCount ?? 0}
          />
        </Stack>
      )}
      <MarkEventAsReviewed
        event={event}
        timeDistanceClassName={styles.eventMarkAsReviewedTimeDistance}
      />
      {isMobile && inventoryAccordionItem && (
        <InventoryEventActionDropdown
          {...inventoryAccordionItem}
          hideYearFromDate={false}
          isEventPage
          useNewMoreIcon
          triggerClassName={styles.eventHeaderDropdownTrigger}
        />
      )}
    </Stack>
  );
};
